import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { Dictionary } from 'lodash';
import _ from 'lodash';
import { UNRATED } from 'src/pages/controls/lookupData';
import type { EnterpriseRiskRegisterFields } from 'src/pages/enterprise-risk/types';
import { useCalculateRiskRating } from 'src/ratings/useCalculateRiskRating';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { handleError } from '@/utils/errorUtils';

import { initializeHeatmapCells } from '../heatmap-widget/heatmapUtils';
import type { HeatmapCellData } from '../heatmap-widget/types';
import type { EnterpriseRiskHeatmapAggregation } from './enterpriseRiskHeatmapSettingsSchema';

type ImpactLikelihood = { Impact: number; Likelihood: number };
type OptionalImpactLikelihood = {
  Impact: null | number | undefined;
  Likelihood: null | number | undefined;
};

type UseGetEnterpriseRiskRatingsDataOptions = {
  controlType: Risk_Assessment_Result_Control_Type_Enum;
  aggregation: EnterpriseRiskHeatmapAggregation;
  enterpriseRisks: readonly EnterpriseRiskRegisterFields[] | undefined;
  getImpactIndex: (value: number) => number | undefined;
  getLikelihoodIndex: (value: number) => number | undefined;
};

type PopulateCellDataValuesOptions = {
  cellData: HeatmapCellData[][];
  rawData: ImpactLikelihood[] | undefined;
  getImpactIndex: (value: number) => number | undefined;
  getLikelihoodIndex: (value: number) => number | undefined;
};

const hasImpactAndLikelihood = (
  rawData: OptionalImpactLikelihood
): rawData is ImpactLikelihood => !!rawData.Impact && !!rawData.Likelihood;

/**
 * Picks the aggregated likelihood/impact dimensions off an enterprise risk's
 * score for the requested side + aggregation. Median dimensions can be
 * fractional (the average of the two middle items), so values are rounded to
 * the nearest matrix position for plotting.
 */
export const getEnterpriseRiskDims = (
  item: EnterpriseRiskRegisterFields,
  controlType: Risk_Assessment_Result_Control_Type_Enum,
  aggregation: EnterpriseRiskHeatmapAggregation
): OptionalImpactLikelihood => {
  const score = item.score;
  const residual =
    controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled;

  const dims = (() => {
    switch (aggregation) {
      case 'median':
        return residual
          ? {
              Impact: score?.ResidualImpactMedian,
              Likelihood: score?.ResidualLikelihoodMedian,
            }
          : {
              Impact: score?.InherentImpactMedian,
              Likelihood: score?.InherentLikelihoodMedian,
            };
      case 'worstCase':
        return residual
          ? {
              Impact: score?.ResidualImpactWorstCase,
              Likelihood: score?.ResidualLikelihoodWorstCase,
            }
          : {
              Impact: score?.InherentImpactWorstCase,
              Likelihood: score?.InherentLikelihoodWorstCase,
            };
      case 'mean':
      default:
        return residual
          ? {
              Impact: score?.ResidualImpactMean,
              Likelihood: score?.ResidualLikelihoodMean,
            }
          : {
              Impact: score?.InherentImpactMean,
              Likelihood: score?.InherentLikelihoodMean,
            };
    }
  })();

  return {
    Impact: dims.Impact == null ? dims.Impact : Math.round(dims.Impact),
    Likelihood:
      dims.Likelihood == null ? dims.Likelihood : Math.round(dims.Likelihood),
  };
};

const populateCellDataValues = ({
  cellData,
  rawData,
  getImpactIndex,
  getLikelihoodIndex,
}: PopulateCellDataValuesOptions) => {
  const counts: Dictionary<number> = _.countBy(
    rawData,
    (item) => `${item.Impact},${item.Likelihood}`
  );
  Object.keys(counts).forEach((k) => {
    const fields = k.split(',');

    const impact = Number(fields[0]);
    const impactIndex = getImpactIndex(impact);

    const likelihood = Number(fields[1]);
    const likelihoodIndex = getLikelihoodIndex(likelihood);

    if (impactIndex === undefined) {
      handleError(`Unable to find index for impact value ${impact}`);

      return;
    }
    if (likelihoodIndex === undefined) {
      handleError(`Unable to find index for likelihood value ${likelihood}`);

      return;
    }

    cellData[likelihoodIndex][impactIndex].value = counts[k];
  });
};

const useCreateCellDataWithBackgroundColor = (
  controlType: Risk_Assessment_Result_Control_Type_Enum
) => {
  const {
    options: { likelihood: likelihoodOptions, impact: impactOptions },
  } = useRiskRatingResolver('standard', { configType: 'risk_assessment' });
  const getComputedRating = useCalculateRiskRating(controlType);

  return initializeHeatmapCells(
    likelihoodOptions.length,
    impactOptions.length,
    (r, c) => {
      const rating = getComputedRating({
        likelihood: likelihoodOptions[r].value,
        impact: impactOptions[c].value,
      });

      // Defensive only — L+I values come from the matrix's own option lists,
      // so a match should always exist. UNRATED is the safe fallback if a
      // misconfigured matrix has gaps.
      return {
        label: rating?.label ?? UNRATED.label,
        background: rating?.color ?? UNRATED.color,
      };
    }
  );
};

export const useGetEnterpriseRiskRatingsData = ({
  controlType,
  aggregation,
  enterpriseRisks,
  getImpactIndex,
  getLikelihoodIndex,
}: UseGetEnterpriseRiskRatingsDataOptions): HeatmapCellData[][] => {
  const cellData = useCreateCellDataWithBackgroundColor(controlType);

  const rawData = enterpriseRisks
    ?.map((item) => getEnterpriseRiskDims(item, controlType, aggregation))
    .filter(hasImpactAndLikelihood);

  populateCellDataValues({
    cellData,
    rawData,
    getImpactIndex,
    getLikelihoodIndex,
  });

  return cellData;
};
